import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {BreadCrumb} from "primereact/breadcrumb";
import {Button} from "primereact/button";
import {ConfirmDialog} from "primereact/confirmdialog";
import {Menu} from "primereact/menu";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import Icon from "../../system/components/icon";
import PageHeaderButton from "../components/page-header-button";
import {setMessage} from "../../system/app-slice";
import {notificationApi} from "../../models/notification/notification-api";
import {pushState} from "../../system/push/push-client";
import ChannelsSection from "./channels-section";
import QuietHoursSection from "./quiet-hours-section";
import ReportsSection from "./reports-section";
import AdvancedSection from "./advanced-section";
import useUnsavedGuard from "./use-unsaved-guard";
import "./notification-settings.css";

// «اعلان‌های من» (task 12, phase 6): level, channels, scheduled reports, quiet hours and the custom matrix.
// Preferences save as one batch through the save bar; push permission, pause and reports act immediately (spec §42).

const LEVELS = [
    {value: "important", title: "مهم", badge: "پیشنهاد زوفینو", description: "فقط چیزهایی که کاری از شما می‌خواهند: واگذاری، منشن، پیام مستقیم، سررسید، Blocker و امنیت."},
    {value: "all", title: "همه", description: "هر رویدادی که اجازه دیدنش را دارید؛ مناسب وقتی می‌خواهید از همه‌چیز باخبر باشید."},
    {value: "custom", title: "سفارشی", description: "برای هر رویداد خودتان مشخص کنید از کدام کانال بیاید."},
];
const EDITABLE = ["mode", "channels", "eventChannels", "quietHoursEnabled", "quietHoursStart", "quietHoursEnd", "quietHoursDays", "allowCriticalDuringQuietHours"];

const pickEditable = (preference) => Object.fromEntries(EDITABLE.map((key) => [key, preference?.[key]]));

function pauseUntil(option) {
    const now = new Date();
    if (option === "hour") return new Date(now.getTime() + 3600 * 1000);
    const tehran = new Intl.DateTimeFormat("en-CA", {timeZone: "Asia/Tehran", year: "numeric", month: "2-digit", day: "2-digit"}).format(now);
    const offsetMs = 3.5 * 3600 * 1000;
    const midnight = new Date(Date.parse(`${tehran}T00:00:00Z`) - offsetMs);
    if (option === "today") return new Date(midnight.getTime() + 24 * 3600 * 1000);
    return new Date(midnight.getTime() + (24 + 8) * 3600 * 1000);
}

function formatUntil(value) {
    try {
        return new Date(value).toLocaleString("fa-IR", {timeZone: "Asia/Tehran", weekday: "long", hour: "2-digit", minute: "2-digit"});
    } catch (error) {
        return "";
    }
}

function NotificationSettings(props) {
    const dispatch = useDispatch();
    const pauseMenu = useRef(null);
    const [settings, setSettings] = useState(null);
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [reportsData, setReportsData] = useState(null);
    const [browserState, setBrowserState] = useState("loading");
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pausing, setPausing] = useState(false);

    const toast = useCallback((severity, content) => dispatch(setMessage({severity, content})), [dispatch]);
    const activeWorkspace = useSelector((state) => state.app.workspaceMembershipData?.workspace);
    const workspaceId = activeWorkspace?.documentId;

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const [data, reports, state] = await Promise.all([
                notificationApi.getSettings(),
                workspaceId ? notificationApi.getReports(workspaceId) : Promise.resolve(null),
                pushState(),
            ]);
            setSettings(data);
            setSaved(data.preference);
            setDraft((current) => current || data.preference);
            setReportsData(reports);
            setBrowserState(state);
        } catch (error) {
            setLoadError("دریافت تنظیمات اعلان‌ها انجام نشد. اتصال را بررسی و دوباره تلاش کنید.");
        }
    }, [workspaceId]);

    useEffect(() => {
        load();
    }, [load]);

    const refreshChannels = useCallback(async () => {
        const [data, state] = await Promise.all([notificationApi.getSettings(), pushState()]);
        setSettings((current) => ({...current, channels: data.channels}));
        setBrowserState(state);
    }, []);

    const dirty = useMemo(() => (
        Boolean(draft && saved) && JSON.stringify(pickEditable(draft)) !== JSON.stringify(pickEditable(saved))
    ), [draft, saved]);
    useUnsavedGuard(dirty);

    const change = (patch) => setDraft((current) => ({...current, ...patch}));

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const result = await notificationApi.saveSettings(pickEditable(draft));
            setSaved(result.preference);
            setDraft(result.preference);
            toast("success", "تنظیمات اعلان‌ها ذخیره شد.");
        } catch (error) {
            toast("error", "ذخیره تنظیمات انجام نشد. دوباره تلاش کنید.");
        } finally {
            setSaving(false);
        }
    };

    const pause = async (option) => {
        if (pausing) return;
        setPausing(true);
        try {
            const until = option ? pauseUntil(option).toISOString() : null;
            const result = await notificationApi.pause(until);
            setSaved((current) => ({...current, pausedUntil: result.pausedUntil}));
            setDraft((current) => ({...current, pausedUntil: result.pausedUntil}));
            toast("info", result.pausedUntil ? `اعلان‌ها تا ${formatUntil(result.pausedUntil)} متوقف شدند.` : "اعلان‌ها دوباره فعال شدند.");
        } catch (error) {
            toast("error", "تغییر وضعیت توقف انجام نشد.");
        } finally {
            setPausing(false);
        }
    };

    const paused = saved?.pausedUntil && new Date(saved.pausedUntil).getTime() > Date.now();
    const pauseItems = [
        {label: "برای ۱ ساعت", command: () => pause("hour")},
        {label: "تا پایان امروز", command: () => pause("today")},
        {label: "تا فردا صبح (۸:۰۰)", command: () => pause("tomorrow")},
    ];

    if (!settings || !draft) {
        return (
            <div className="h-full flex align-items-center justify-content-center" dir="rtl">
                {loadError ? (
                    <div className="flex flex-column align-items-center gap-3">
                        <Message severity="error" text={loadError} className="text-xs"/>
                        <Button label="تلاش دوباره" icon="pi pi-refresh" size="small" className="text-xs gap-1" onClick={load}/>
                    </div>
                ) : <ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/>}
            </div>
        );
    }

    return (
        <div className="notification-settings shabnam-font h-full min-h-0 overflow-auto" dir="rtl">
            <ConfirmDialog className="notification-settings-dialog"/>
            <div className="ns-container px-3">
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={[{label: "اعلان‌ها", url: "#/inbox"}, {label: "اعلان‌های من"}]}/>
            <PageHeaderButton
                title={props.name || "notification-settings"}
                description={props.description}
                end={(
                    <>
                        <Menu model={pauseItems} popup ref={pauseMenu} className="rtl text-xs"/>
                        {paused ? (
                            <Button label={`متوقف تا ${formatUntil(saved.pausedUntil)} · ادامه`} icon="pi pi-play" size="small" severity="warning" className="text-xs gap-1" loading={pausing} onClick={() => pause(null)}/>
                        ) : (
                            <Button label="توقف موقت اعلان‌ها" icon="pi pi-pause" size="small" outlined className="text-xs gap-1" loading={pausing} onClick={(event) => pauseMenu.current.toggle(event)}/>
                        )}
                    </>
                )}
            />

            <div className="pb-8 flex flex-column gap-3">
                {paused && (
                    <Message severity="warn" className="text-xs w-full justify-content-start" text="اعلان‌های خارج از زوفینو موقتاً متوقف‌اند (به‌جز هشدارهای امنیتی حیاتی). داخل زوفینو همه‌چیز ثبت می‌شود."/>
                )}

                <section className="ns-card">
                    <h2 className="ns-card-title"><span className="ns-icon"><i className="pi pi-sliders-h"/></span>سطح اعلان‌ها</h2>
                    <p className="ns-card-description">یک نقطه شروع انتخاب کنید؛ در حالت «سفارشی» رویداد و کانال هر اعلان را جداگانه تعیین می‌کنید.</p>
                    <div className="grid m-0" role="radiogroup" aria-label="سطح اعلان‌ها">
                        {LEVELS.map((level) => {
                            const active = draft.mode === level.value;
                            return (
                                <div key={level.value} className="col-12 md:col-4 p-1">
                                    <button
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => change({mode: level.value})}
                                        className={`ns-level ${active ? "is-active" : ""}`}
                                    >
                                        <span className="flex align-items-center justify-content-between gap-2">
                                            <strong className={`text-sm ${active ? "text-primary-700" : "text-900"}`}>{level.title}</strong>
                                            {level.badge && <span className="text-2xs text-primary-700 bg-primary-100 border-round px-2 py-1">{level.badge}</span>}
                                        </span>
                                        <small className="text-xs text-600 line-height-3">{level.description}</small>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <ChannelsSection
                    channels={settings.channels}
                    preference={draft}
                    onPreferenceChange={change}
                    browserState={browserState}
                    onRefresh={refreshChannels}
                    toast={toast}
                />

                <div className="grid m-0">
                    <div className="col-12 lg:col-6 p-0 lg:pl-2 mb-3 lg:mb-0">
                        <ReportsSection reportsData={reportsData} workspace={activeWorkspace} onReportsChange={(reports) => setReportsData((current) => ({...current, reports}))} toast={toast}/>
                    </div>
                    <div className="col-12 lg:col-6 p-0 lg:pr-2">
                        <QuietHoursSection preference={draft} onPreferenceChange={change}/>
                    </div>
                </div>

                <AdvancedSection groups={settings.groups} events={settings.events} preference={draft} managerial={settings.managerial} onPreferenceChange={change}/>
            </div>
            </div>

            {dirty && (
                <div className="fixed bottom-0 left-0 right-0 flex justify-content-center p-3" style={{zIndex: 30, pointerEvents: "none"}}>
                    <div className="notification-settings ns-save-bar flex align-items-center gap-3 px-3 py-2" style={{pointerEvents: "auto"}}>
                        <span className="text-xs">تغییرات ذخیره‌نشده دارید.</span>
                        <Button label="لغو تغییرات" size="small" text className="text-xs text-white" disabled={saving} onClick={() => setDraft(saved)}/>
                        <Button label="ذخیره تغییرات" icon="pi pi-check" size="small" className="text-xs gap-1" loading={saving} onClick={save}/>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationSettings;
